import { CONFIG } from './config.js';
import { createHttpClient } from './api/httpClient.js';
import { HackerNewsRepository } from './api/hackerNewsRepository.js';
import { createStore, createInitialState } from './store/store.js';
import { createNewsService } from './services/newsService.js';
import { createNewsListView } from './ui/newsListView.js';
import { initTheme } from './ui/theme.js';
import { showToast } from './ui/toast.js';
import { debounce, qs } from './ui/dom.js';

/**
 * Composition root.
 *
 * Qui — e solo qui — i pezzi vengono messi insieme:
 *
 *   HTTP client  →  Repository  →  Service  →  Store  →  View
 *
 * Ogni modulo riceve le proprie dipendenze dall'esterno, quindi nessuno di
 * loro ha bisogno del browser per essere testato.
 */

function bootstrap() {
  // --- Riferimenti al DOM ------------------------------------------------
  const elements = {
    list: qs('#news-list'),
    skeleton: qs('#skeleton'),
    empty: qs('#empty'),
    error: qs('#error'),
    retryButton: qs('#retry'),
    loadMoreButton: qs('#load-more'),
    loadMoreLabel: qs('[data-load-more-label]'),
    progress: qs('[data-progress]'),
    listMeta: qs('[data-list-meta]'),
    stats: qs('#stats'),
    domains: qs('#domains'),
    search: qs('#search'),
    sort: qs('#sort'),
    favoritesFilter: qs('#favorites-filter'),
    favoritesCount: qs('[data-favorites-count]'),
    refreshButton: qs('#refresh'),
    themeToggle: qs('#theme-toggle'),
    toasts: qs('#toasts'),
  };

  // --- Composizione delle dipendenze ------------------------------------
  const httpClient = createHttpClient();
  const repository = new HackerNewsRepository(httpClient);
  const store = createStore(createInitialState());
  const service = createNewsService({ repository, store, pageSize: CONFIG.PAGE_SIZE });

  const view = createNewsListView(elements, {
    onToggleFavorite: (id) => {
      const added = service.toggleFavorite(id);
      showToast(elements.toasts, added ? 'Salvata nei preferiti ★' : 'Rimossa dai preferiti', {
        variant: added ? 'success' : 'info',
        duration: 1800,
      });
    },
  });

  // --- La vista osserva lo store ----------------------------------------
  store.subscribe((state) => view.render(state));

  // --- Interazioni utente ------------------------------------------------
  elements.loadMoreButton?.addEventListener('click', async () => {
    try {
      const added = await service.loadMore();
      if (added.length === 0) {
        showToast(elements.toasts, 'Non ci sono altre news da caricare', { variant: 'info' });
      }
    } catch {
      showToast(elements.toasts, 'Caricamento non riuscito, riprova', { variant: 'error' });
    }
  });

  elements.refreshButton?.addEventListener('click', async () => {
    try {
      await service.refresh();
      showToast(elements.toasts, 'News aggiornate', { variant: 'success' });
    } catch {
      showToast(elements.toasts, 'Aggiornamento non riuscito', { variant: 'error' });
    }
  });

  elements.retryButton?.addEventListener('click', () => {
    service.refresh().catch(() => {});
  });

  // Il debounce evita un re-render a ogni singolo tasto premuto.
  elements.search?.addEventListener(
    'input',
    debounce((event) => service.setSearch(event.target.value), 220),
  );

  elements.sort?.addEventListener('change', (event) => service.setSort(event.target.value));

  elements.favoritesFilter?.addEventListener('click', () => service.toggleFavoritesFilter());

  // --- Avvio -------------------------------------------------------------
  initTheme(elements.themeToggle);
  service.restoreFavorites();
  view.render(store.getState());

  service.loadInitial().catch((error) => {
    console.error('[main] caricamento iniziale fallito:', error);
    showToast(elements.toasts, 'Impossibile caricare le news', { variant: 'error' });
  });
}

// I moduli ES sono deferred: di norma il DOM è già pronto, ma il controllo
// rende l'avvio sicuro anche se lo script venisse incluso diversamente.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
